<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة تواصل معنا</h1>
        <p class="mt-2 text-sm text-gray-700">تعديل معلومات التواصل للفرع</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="mt-8 text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p class="mt-2 text-gray-600">جاري التحميل...</p>
    </div>

    <!-- Form -->
    <div v-else class="mt-8 bg-white shadow rounded-lg p-6">
      <form @submit.prevent="handleSave">
        <div class="space-y-6">
          <!-- Contact Information Section -->
          <div class="border-b border-gray-200 pb-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">معلومات التواصل الأساسية</h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <!-- Email -->
              <div>
                <label for="contact_email" class="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني
                </label>
                <input
                  v-model="formData.contact_email"
                  type="email"
                  id="contact_email"
                  placeholder="مثال: info@example.com"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <!-- Phone -->
              <div>
                <label for="contact_phone" class="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف
                </label>
                <input
                  v-model="formData.contact_phone"
                  type="tel"
                  id="contact_phone"
                  placeholder="مثال: +966501234567"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <!-- Address -->
            <div class="mt-6">
              <label for="contact_address" class="block text-sm font-medium text-gray-700 mb-2">
                العنوان
              </label>
              <textarea
                v-model="formData.contact_address"
                id="contact_address"
                rows="3"
                placeholder="مثال: الرياض، حي النرجس، شارع الملك فهد"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              ></textarea>
            </div>

            <!-- WhatsApp -->
            <div class="mt-6">
              <label for="contact_whatsapp" class="block text-sm font-medium text-gray-700 mb-2">
                رقم واتساب
              </label>
              <input
                v-model="formData.contact_whatsapp"
                type="tel"
                id="contact_whatsapp"
                placeholder="مثال: +966501234567"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <!-- Social Media Section -->
          <div class="border-b border-gray-200 pb-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">وسائل التواصل الاجتماعي</h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <!-- Facebook -->
              <div>
                <label for="contact_facebook" class="block text-sm font-medium text-gray-700 mb-2">
                  رابط فيسبوك
                </label>
                <input
                  v-model="formData.contact_facebook"
                  type="url"
                  id="contact_facebook"
                  placeholder="مثال: https://facebook.com/example"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <!-- Twitter -->
              <div>
                <label for="contact_twitter" class="block text-sm font-medium text-gray-700 mb-2">
                  رابط تويتر
                </label>
                <input
                  v-model="formData.contact_twitter"
                  type="url"
                  id="contact_twitter"
                  placeholder="مثال: https://twitter.com/example"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <!-- Instagram -->
              <div>
                <label for="contact_instagram" class="block text-sm font-medium text-gray-700 mb-2">
                  رابط إنستغرام
                </label>
                <input
                  v-model="formData.contact_instagram"
                  type="url"
                  id="contact_instagram"
                  placeholder="مثال: https://instagram.com/example"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <!-- Website -->
              <div>
                <label for="contact_website" class="block text-sm font-medium text-gray-700 mb-2">
                  رابط الموقع الإلكتروني
                </label>
                <input
                  v-model="formData.contact_website"
                  type="url"
                  id="contact_website"
                  placeholder="مثال: https://www.example.com"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-8 flex justify-end gap-3">
          <button
            type="button"
            @click="handleReset"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            إعادة تعيين
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="px-4 py-2 bg-customHafadhColor text-white rounded-md shadow-sm text-sm font-medium hover:bg-customHafadhColor/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ saving ? 'جاري الحفظ...' : 'حفظ التغييرات' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'ContactUsManagement',
  data() {
    return {
      loading: false,
      saving: false,
      formData: {
        contact_email: '',
        contact_phone: '',
        contact_address: '',
        contact_whatsapp: '',
        contact_facebook: '',
        contact_twitter: '',
        contact_instagram: '',
        contact_website: ''
      },
      originalData: {
        contact_email: '',
        contact_phone: '',
        contact_address: '',
        contact_whatsapp: '',
        contact_facebook: '',
        contact_twitter: '',
        contact_instagram: '',
        contact_website: ''
      },
      alert: {
        show: false,
        type: 'success',
        message: ''
      }
    }
  },
  mounted() {
    this.loadTenantData()
  },
  methods: {
    async loadTenantData() {
      this.loading = true
      try {
        // Get tenant_id from JWT token
        let tenantId = null
        try {
          const token = localStorage.getItem('token')
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]))
            tenantId = payload.tenantId || payload.tenant_id || null
          }
        } catch (e) {
          console.warn('Could not extract tenantId from token:', e)
          this.showAlert('danger', 'فشل في تحديد الفرع')
          this.loading = false
          return
        }

        if (!tenantId) {
          this.showAlert('danger', 'لم يتم العثور على معرف الفرع')
          this.loading = false
          return
        }

        // Fetch tenant data
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.tenants_show + tenantId,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        console.log('ContactUsManagement: Load tenant data response', {
          status: response.status,
          hasData: !!response.data,
          responseStructure: {
            hasResult: !!response.data?.result,
            hasResultData: !!response.data?.result?.data,
            hasData: !!response.data?.data,
          }
        })

        // Extract tenant data from response
        let tenant = null
        if (response.data?.result?.data) {
          tenant = response.data.result.data
        } else if (response.data?.data) {
          tenant = response.data.data
        } else if (response.data?.result) {
          tenant = response.data.result
        } else {
          tenant = response.data
        }

        console.log('ContactUsManagement: Extracted tenant data', {
          hasTenant: !!tenant,
          contactFields: tenant ? {
            contact_email: tenant.contact_email || tenant.contactEmail || 'null',
            contact_phone: tenant.contact_phone || tenant.contactPhone || 'null',
            contact_address: tenant.contact_address || tenant.contactAddress || 'null',
            contact_whatsapp: tenant.contact_whatsapp || tenant.contactWhatsapp || 'null',
            contact_facebook: tenant.contact_facebook || tenant.contactFacebook || 'null',
            contact_twitter: tenant.contact_twitter || tenant.contactTwitter || 'null',
            contact_instagram: tenant.contact_instagram || tenant.contactInstagram || 'null',
            contact_website: tenant.contact_website || tenant.contactWebsite || 'null',
          } : null
        })

        if (tenant) {
          this.formData = {
            contact_email: tenant.contact_email || tenant.contactEmail || '',
            contact_phone: tenant.contact_phone || tenant.contactPhone || '',
            contact_address: tenant.contact_address || tenant.contactAddress || '',
            contact_whatsapp: tenant.contact_whatsapp || tenant.contactWhatsapp || '',
            contact_facebook: tenant.contact_facebook || tenant.contactFacebook || '',
            contact_twitter: tenant.contact_twitter || tenant.contactTwitter || '',
            contact_instagram: tenant.contact_instagram || tenant.contactInstagram || '',
            contact_website: tenant.contact_website || tenant.contactWebsite || ''
          }
          this.originalData = { ...this.formData }
          
          console.log('ContactUsManagement: Form data initialized', this.formData)
        }
      } catch (error) {
        console.error('Failed to load tenant data', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تحميل بيانات الفرع')
      } finally {
        this.loading = false
      }
    },
    async handleSave() {
      this.saving = true
      try {
        // Get tenant_id from JWT token
        let tenantId = null
        try {
          const token = localStorage.getItem('token')
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]))
            tenantId = payload.tenantId || payload.tenant_id || null
          }
        } catch (e) {
          console.warn('Could not extract tenantId from token:', e)
          this.showAlert('danger', 'فشل في تحديد الفرع')
          this.saving = false
          return
        }

        if (!tenantId) {
          this.showAlert('danger', 'لم يتم العثور على معرف الفرع')
          this.saving = false
          return
        }

        // Use FormData to send data
        const formData = new FormData()
        formData.append('contact_email', this.formData.contact_email || '')
        formData.append('contact_phone', this.formData.contact_phone || '')
        formData.append('contact_address', this.formData.contact_address || '')
        formData.append('contact_whatsapp', this.formData.contact_whatsapp || '')
        formData.append('contact_facebook', this.formData.contact_facebook || '')
        formData.append('contact_twitter', this.formData.contact_twitter || '')
        formData.append('contact_instagram', this.formData.contact_instagram || '')
        formData.append('contact_website', this.formData.contact_website || '')
        formData.append('_method', 'PUT')

        console.log('ContactUsManagement: Sending data', {
          tenantId,
          formDataValues: {
            contact_email: this.formData.contact_email,
            contact_phone: this.formData.contact_phone,
            contact_address: this.formData.contact_address,
            contact_whatsapp: this.formData.contact_whatsapp,
            contact_facebook: this.formData.contact_facebook,
            contact_twitter: this.formData.contact_twitter,
            contact_instagram: this.formData.contact_instagram,
            contact_website: this.formData.contact_website,
          }
        })

        // Update tenant
        const response = await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.tenants_update + tenantId,
          formData,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            },
            transformRequest: [(data) => {
              return data
            }]
          }
        )

        console.log('ContactUsManagement: Save response', {
          status: response.status,
          data: response.data
        })

        // Reload data to get updated values
        await this.loadTenantData()

        this.originalData = { ...this.formData }
        this.showAlert('success', 'تم حفظ التغييرات بنجاح')
      } catch (error) {
        console.error('Failed to save tenant data', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في حفظ التغييرات')
      } finally {
        this.saving = false
      }
    },
    handleReset() {
      this.formData = { ...this.originalData }
      this.showAlert('info', 'تم إعادة تعيين النموذج')
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    }
  }
})
</script>

<style scoped>
.enabled-button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors;
}
</style>

